import { useCallback, useMemo, useRef, useState } from 'react';
import useLabelingStore from '../store/useDetectionStore.js';
import useModelManagementStore from '../../../modelManagement/store/useModelManagementStore';
import { preLabelWithTrainedModelAPI } from '../api/detectionLabelApi.js';
import {
  buildAiAnnotations,
  getImageBlob,
  isAbortError,
  isUnlabeled,
} from '../utils/aiSuggestions.js';

/**
 * Pre-labeling with the project's OWN trained versions — the model-in-the-loop
 * half of the labeling flow: train a version, use it to draft the labels for
 * the next one, correct what it got wrong, retrain.
 *
 * It reuses the AI-suggestion pipeline wholesale: boxes land as `isAI: true`,
 * render dashed, and are accepted or discarded with the same controls as the
 * open-vocabulary auto-detect. Editing one accepts it.
 *
 * Class resolution is deliberately STRICT. The predictions come from a version
 * of this same project, so their names should already exist here; a name that
 * does not match means the class list has drifted between versions, and
 * silently re-creating it would be worse than reporting it as skipped.
 */

const DEFAULT_CONFIDENCE = 0.25;

/** A version can only pre-label once its training finished. */
const isTrained = (version) => String(version?.trainingStatus || '').toLowerCase() === 'complete';

/** Alias term (lowercased) -> class id, mirroring the auto-detect mapping. */
const buildProbeMap = (classes) => {
  const probeToClassId = new Map();
  (classes || []).forEach((cls) => {
    [cls.name, ...String(cls.aiAliases || '').split(',')]
      .map((term) => String(term || '').trim().toLowerCase())
      .filter(Boolean)
      .forEach((term) => {
        if (!probeToClassId.has(term)) probeToClassId.set(term, cls.id);
      });
  });
  return probeToClassId;
};

/** inference_json returns bbox as [x1,y1,x2,y2]; the builder wants an object. */
const toDetections = (predictions) =>
  (predictions || [])
    .filter((p) => Array.isArray(p?.bbox) && p.bbox.length === 4)
    .map((p) => ({
      bbox: { x1: p.bbox[0], y1: p.bbox[1], x2: p.bbox[2], y2: p.bbox[3] },
      class: p.className ?? String(p.classId ?? ''),
      confidence: p.confidence,
    }));

const useModelPreLabel = (saveState) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, found, currentName}
  const [error, setError] = useState('');
  const [confidence, setConfidence] = useState(DEFAULT_CONFIDENCE);
  const [versionId, setVersionId] = useState('');

  const stopRequestedRef = useRef(false);
  const abortRef = useRef(null);

  const { modelInfo } = useModelManagementStore();
  const { images, currentImageIndex } = useLabelingStore();

  const modelType = modelInfo?.model_type || modelInfo?.modelType || '';

  /** Trained versions of this model, newest first — the pre-label candidates. */
  const trainedVersions = useMemo(() => {
    const versions = Array.isArray(modelInfo?.versions) ? modelInfo.versions : [];
    return versions.filter(isTrained).slice().reverse();
  }, [modelInfo]);

  const selectedVersionId = useMemo(() => {
    if (versionId && trainedVersions.some((v) => v._id === versionId)) return versionId;
    return trainedVersions[0]?._id || '';
  }, [versionId, trainedVersions]);

  const stop = useCallback(() => {
    stopRequestedRef.current = true;
    setIsStopping(true);
    abortRef.current?.abort();
  }, []);

  /**
   * Run the trained model over one image and inject what it finds.
   * Returns the number of suggestions added (0 on any failure).
   */
  const preLabelImage = useCallback(async (imageIndex) => {
    const store = useLabelingStore.getState();
    const image = store.images[imageIndex];
    if (!image || !selectedVersionId) return 0;

    const width = Number(image.originalWidth) || 0;
    const height = Number(image.originalHeight) || 0;
    if (!width || !height) return 0;

    const controller = new AbortController();
    abortRef.current = controller;

    const blob = await getImageBlob(image);
    const response = await preLabelWithTrainedModelAPI({
      blob,
      fileName: image.fileName,
      modelType,
      modelDataInfoId: selectedVersionId,
      confidence,
      signal: controller.signal,
    });

    const detections = toDetections(response?.data?.predictions);
    if (detections.length === 0) return 0;

    const { anns } = buildAiAnnotations(
      detections,
      { width, height },
      image.annotations || [],
      {
        classes: store.classes,
        strict: true,
        probeToClassId: buildProbeMap(store.classes),
      },
    );

    if (anns.length > 0) {
      store.applyAiAnnotations(imageIndex, anns);
    }
    return anns.length;
  }, [confidence, modelType, selectedVersionId]);

  const preLabelCurrent = useCallback(async () => {
    if (isRunning || !selectedVersionId) return;
    stopRequestedRef.current = false;
    setIsStopping(false);
    setError('');
    setIsRunning(true);
    setProgress(null);

    try {
      saveState?.();
      const found = await preLabelImage(useLabelingStore.getState().currentImageIndex);
      if (found === 0) setError('noDetections');
    } catch (err) {
      if (!isAbortError(err)) {
        setError(err?.response?.data?.detail || err?.message || 'failed');
      }
    } finally {
      abortRef.current = null;
      setIsRunning(false);
      setIsStopping(false);
    }
  }, [isRunning, preLabelImage, saveState, selectedVersionId]);

  const preLabelUnlabeled = useCallback(async () => {
    if (isRunning || !selectedVersionId) return;
    stopRequestedRef.current = false;
    setIsStopping(false);
    setError('');

    const store = useLabelingStore.getState();
    const targets = store.images
      .map((img, index) => ({ img, index }))
      .filter(({ img }) => isUnlabeled(img));

    if (targets.length === 0) return;

    // the open image may be one of the targets — snapshot it so its
    // suggestions stay undoable
    saveState?.();

    setIsRunning(true);
    setProgress({ done: 0, total: targets.length, found: 0, currentName: '' });

    let found = 0;
    try {
      for (let i = 0; i < targets.length; i += 1) {
        if (stopRequestedRef.current) break;
        const { img, index } = targets[i];
        setProgress({ done: i, total: targets.length, found, currentName: img.fileName });
        try {
          found += await preLabelImage(index);
        } catch (err) {
          if (isAbortError(err)) break; // Stop, not a failure
          // one bad image must not sink the batch
          console.warn('Pre-label failed for', img.fileName, err);
        }
      }
      setProgress({ done: targets.length, total: targets.length, found, currentName: '' });
    } finally {
      abortRef.current = null;
      setIsRunning(false);
      setIsStopping(false);
    }
  }, [isRunning, preLabelImage, saveState, selectedVersionId]);

  const unlabeledCount = useMemo(() => images.filter(isUnlabeled).length, [images]);

  return {
    trainedVersions,
    selectedVersionId,
    setVersionId,
    confidence,
    setConfidence,
    isRunning,
    isStopping,
    progress,
    error,
    clearError: () => setError(''),
    unlabeledCount,
    currentImageIndex,
    preLabelCurrent,
    preLabelUnlabeled,
    stop,
  };
};

export default useModelPreLabel;
